/**
 * Language Detector Service
 * Smart language detection with domain-based overrides
 * Ensures campaign language matches target audience, not just website content
 */

export interface LanguageDetectionResult {
  campaignLanguage: string;        // 'de' - Language for campaign/keywords
  campaignLanguageName: string;    // 'German' - Display name
  contentLanguage: string;          // 'en' - Actual website language
  hasLanguageMismatch: boolean;     // Website ≠ Campaign language
  reasoning: string;                // Why this language was chosen
  tldBased: boolean;                // Was decision based on TLD?
}

export const languageDetector = {
  /**
   * Detect campaign language with smart domain-based defaults
   * RULE: .de/.at/.ch Domain → ALWAYS German for campaign (target audience)
   */
  detectCampaignLanguage(
    url: string,
    scrapedLanguage: string | undefined | null,
    contentLanguages: string[] = []
  ): LanguageDetectionResult {
    // Ensure scrapedLanguage is a valid string
    const safeLang = (scrapedLanguage && typeof scrapedLanguage === 'string') 
      ? scrapedLanguage 
      : 'en';
    
    const domain = new URL(url).hostname;
    const tld = domain.split('.').pop()?.toLowerCase() || '';
    
    // RULE 1: DACH Domain → IMMER Deutsch für Kampagne
    if (tld === 'de' || tld === 'at' || tld === 'ch') {
      const hasEnglishContent = safeLang === 'en' || contentLanguages.includes('en');
      
      return {
        campaignLanguage: 'de',
        campaignLanguageName: 'German',
        contentLanguage: safeLang,
        hasLanguageMismatch: safeLang !== 'de',
        reasoning: hasEnglishContent 
          ? `.${tld} Domain → Deutsch für deutsche Zielgruppe (Website hat Englisch)`
          : `.${tld} Domain → Deutsch für deutsche Zielgruppe`,
        tldBased: true
      };
    }
    
    // RULE 2: UK Domain → English
    if (tld === 'uk' || domain.endsWith('.co.uk')) {
      return {
        campaignLanguage: 'en',
        campaignLanguageName: 'English',
        contentLanguage: safeLang,
        hasLanguageMismatch: safeLang !== 'en',
        reasoning: '.uk Domain → English für UK Zielgruppe',
        tldBased: true
      };
    }
    
    // RULE 3: Other European TLDs
    const europeanTLDs = {
      'fr': { code: 'fr', name: 'French' },
      'es': { code: 'es', name: 'Spanish' },
      'it': { code: 'it', name: 'Italian' },
      'nl': { code: 'nl', name: 'Dutch' },
      'pl': { code: 'pl', name: 'Polish' },
      'pt': { code: 'pt', name: 'Portuguese' },
      'se': { code: 'sv', name: 'Swedish' },
      'dk': { code: 'da', name: 'Danish' },
      'no': { code: 'no', name: 'Norwegian' },
      'fi': { code: 'fi', name: 'Finnish' }
    };
    
    if (europeanTLDs[tld]) {
      const lang = europeanTLDs[tld];
      return {
        campaignLanguage: lang.code,
        campaignLanguageName: lang.name,
        contentLanguage: safeLang,
        hasLanguageMismatch: safeLang !== lang.code,
        reasoning: `.${tld} Domain → ${lang.name} für lokale Zielgruppe`,
        tldBased: true
      };
    }
    
    // RULE 4: Multi-Language Site → Use TLD as tiebreaker
    if (contentLanguages.length > 1) {
      const primaryLang = contentLanguages[0];
      const langName = this.getLanguageName(primaryLang);
      
      return {
        campaignLanguage: primaryLang,
        campaignLanguageName: langName,
        contentLanguage: safeLang,
        hasLanguageMismatch: false,
        reasoning: `Multi-language site (${contentLanguages.length} languages), using primary: ${langName}`,
        tldBased: false
      };
    }
    
    // RULE 5: Default to scraped language
    const langName = this.getLanguageName(safeLang);
    return {
      campaignLanguage: safeLang,
      campaignLanguageName: langName,
      contentLanguage: safeLang,
      hasLanguageMismatch: false,
      reasoning: `Single language site: ${langName}`,
      tldBased: false
    };
  },

  /**
   * Get TLD-based language (fallback)
   */
  getTLDLanguage(tld: string): { code: string; name: string } {
    const tldMap: { [key: string]: { code: string; name: string } } = {
      'de': { code: 'de', name: 'German' },
      'at': { code: 'de', name: 'German' },
      'ch': { code: 'de', name: 'German' },
      'uk': { code: 'en', name: 'English' },
      'com': { code: 'en', name: 'English' },
      'fr': { code: 'fr', name: 'French' },
      'es': { code: 'es', name: 'Spanish' },
      'it': { code: 'it', name: 'Italian' },
      'nl': { code: 'nl', name: 'Dutch' }
    };
    
    return tldMap[tld] || { code: 'en', name: 'English' };
  },

  /**
   * Get language name from code
   */
  getLanguageName(code: string | undefined | null): string {
    // Handle undefined/null/non-string input
    if (!code || typeof code !== 'string') {
      return 'English';
    }
    
    const languageMap: { [key: string]: string } = {
      'de': 'German',
      'en': 'English',
      'fr': 'French',
      'es': 'Spanish',
      'it': 'Italian',
      'nl': 'Dutch',
      'pt': 'Portuguese',
      'pl': 'Polish',
      'sv': 'Swedish',
      'da': 'Danish',
      'no': 'Norwegian',
      'fi': 'Finnish',
      'ja': 'Japanese',
      'zh': 'Chinese',
      'ko': 'Korean',
      'ru': 'Russian'
    };
    
    return languageMap[code.toLowerCase()] || 'English';
  },

  /**
   * Validate if keywords match campaign language
   */
  validateKeywordLanguage(keywords: string[], expectedLanguage: string): {
    valid: boolean;
    invalidKeywords: string[];
    confidence: number;
  } {
    // Simple validation: Check for language-specific words
    const germanIndicators = ['der', 'die', 'das', 'und', 'für', 'mit', 'bei', 'von'];
    const englishIndicators = ['the', 'and', 'for', 'with', 'at', 'from', 'to'];
    
    const invalidKeywords: string[] = [];
    
    keywords.forEach(keyword => {
      const lowerKeyword = keyword.toLowerCase();
      
      if (expectedLanguage === 'de') {
        // Should be German - check for English indicators
        const hasEnglish = englishIndicators.some(ind => lowerKeyword.includes(` ${ind} `));
        if (hasEnglish && !germanIndicators.some(ind => lowerKeyword.includes(ind))) {
          invalidKeywords.push(keyword);
        }
      } else if (expectedLanguage === 'en') {
        // Should be English - check for German indicators
        const hasGerman = germanIndicators.some(ind => lowerKeyword.includes(` ${ind} `));
        if (hasGerman && !englishIndicators.some(ind => lowerKeyword.includes(ind))) {
          invalidKeywords.push(keyword);
        }
      }
    });
    
    const confidence = ((keywords.length - invalidKeywords.length) / keywords.length) * 100;
    
    return {
      valid: invalidKeywords.length === 0,
      invalidKeywords,
      confidence
    };
  }
};

